"use client";

import { useSyncExternalStore } from "react";

import { safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";
import { getSEOIntentConfig, normalizeSEOIntent, type SEOIntentConfig } from "@/lib/seo-intents";
import { AI_ASTROLOGER_ONBOARDING_CONTRACT } from "./onboarding-contract";
import styles from "./ConsultationHero.module.css";

/**
 * A concern is an optional refinement on the chat-intent surface. The choices
 * are predefined, so analytics never receives a seeker's free-text concern.
 */
function trackTopicSelection(properties: Record<string, unknown>) {
  void import("@/lib/analytics-events").then(({ trackEvent }) => {
    trackEvent("topic_select_changed", properties);
  });
}

const subscribeToHydration = () => () => {};
const clientHydrationSnapshot = () => true;
const serverHydrationSnapshot = () => false;

function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribeToHydration,
    clientHydrationSnapshot,
    serverHydrationSnapshot
  );
}

export default function TopicSelect({
  source = "/ai-astrologer/",
  onIntentCaptured,
  onSkip,
}: {
  source?: string;
  onIntentCaptured: (entry: ConcernSelection) => void;
  onSkip: () => void;
}) {
  const isHydrated = useHydrated();

  function selectConcern(
    topic: (typeof AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.options)[number]
  ) {
    const intent = normalizeSEOIntent(topic.seoIntent);
    const config = getSEOIntentConfig(intent);
    const capturedConfig = { ...config, prompt: topic.prompt };
    try {
      // The downstream ChatPanel reads this value as a backend concern. Keep
      // the SEO config in the controlled entry below, and persist the native
      // concern here so the first greeting actually answers the chosen pattern.
      if (topic.concern) {
        safeSessionSet("seo_entry_intent", topic.concern);
      } else {
        safeSessionRemove("seo_entry_intent");
      }
      safeSessionSet("seo_entry_prompt", topic.prompt);
      safeSessionSet("seo_entry_topic", topic.key);
      safeSessionSet("seo_entry_path", source);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // sessionStorage may be disabled (private browsing); event still fires.
    }
    onIntentCaptured({
      config: capturedConfig,
      sourcePath: source,
      topicKey: topic.key,
      topicTitle: topic.title,
      topicLens: topic.lens,
    });
    trackTopicSelection({
      intent: config.intent,
      topic_key: topic.key,
      // Stable, non-PII journey dimensions. The customer-facing title/prompt
      // remain in the governed onboarding contract and do not need to be
      // duplicated into analytics.
      source_path: source,
      source_page: source,
    });
  }

  function skipConcern() {
    clearConcernSelection();
    onSkip();
    trackTopicSelection({
      topic_key: "skip",
      source_path: source,
      source_page: source,
    });
  }

  return (
    <fieldset
      className="m-0 border-0 p-0"
      data-hydrated={isHydrated ? "true" : "false"}
      data-testid="ai-astrologer-topic-select"
    >
      <legend className={styles.topicLegend}>
        {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.legend}
      </legend>
      <div className={styles.topicMeta}>
        <p className={styles.topicHelper}>{AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.helper}</p>
        <button
          type="button"
          disabled={!isHydrated}
          onClick={skipConcern}
          className={styles.skipButton}
        >
          {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.skipLabel}
        </button>
      </div>
      <div className={styles.topicGrid}>
        {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.options.map((topic) => (
          <button
            key={topic.key}
            type="button"
            disabled={!isHydrated}
            onClick={() => selectConcern(topic)}
            className={styles.topicButton}
          >
            <span className={styles.topicTitle}>{topic.title}</span>
            <span className={styles.topicLens}>{topic.lens}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export type ConcernSelection = {
  config: SEOIntentConfig;
  sourcePath: string;
  topicKey: string;
  topicTitle: string;
  topicLens: string;
};

export function clearConcernSelection() {
  for (const key of [
    "seo_entry_intent",
    "seo_entry_prompt",
    "seo_entry_topic",
    "seo_entry_path",
    "seo_entry_page_category",
    "seo_entry_primary_offer",
    "seo_entry_report_plan_id",
    "seo_entry_auto_send",
  ]) {
    safeSessionRemove(key);
  }
}
